import { Icon } from "@circle-react-shared/Icon";
import type { Space } from "@circle-react/types";
import classNames from "classnames";
import fuzzysort from "fuzzysort";
import { groupBy, map, trim } from "lodash";
import type { ChangeEvent, LegacyRef } from "react";
import { useState } from "react";
import type { MouseEvent } from "react";

import { t } from "@/i18n-js/instance";
import {
  anchorClassNames,
  legacyClassNames,
} from "@/react/helpers/cssAnchorHelpers";
import { useFocusOnLoad } from "@/react/hooks/useFocusOnLoad";

import { DropdownMenu_DEPRECATED } from "../DropdownMenu";
import { NoResultsListItem } from "./NoResultsListItem";
import { SpaceList } from "./SpaceList";

interface SpacesDropdown {
  className?: string;
  handleClose?: () => void;
  onChange?: (id: number | null) => void;
  open?: boolean;
  placeholder?: string;
  shouldDisplayHomepage?: boolean;
  spaces?: Space[];
}

export const SpacesDropdown = ({
  open,
  handleClose,
  onChange,
  spaces = [],
  shouldDisplayHomepage,
  className,
  placeholder = t("search_space_placeholder"),
}: SpacesDropdown) => {
  const inputRef: LegacyRef<HTMLInputElement> = useFocusOnLoad([open]);
  const [searchQuery, setSearchQuery] = useState("");

  const onSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(trim(event.target.value));
  };

  const handleSpace = (_: MouseEvent<HTMLButtonElement>, id: number | null) => {
    onChange?.(id);
    handleClose?.();
  };

  let filterSpaces = spaces;

  if (searchQuery) {
    filterSpaces = fuzzysort
      .go(searchQuery, spaces, { keys: ["name", "space_group_name"] })
      .map(res => res.obj);
  }

  const groupedSpaces = groupBy(filterSpaces, "space_group_name");

  return (
    <DropdownMenu_DEPRECATED
      open={open}
      handleClose={handleClose}
      className={classNames(
        anchorClassNames("react-space-selector-dropdown__wrapper"),
        className,
      )}
    >
      <div className="p-4 pb-0">
        <input
          id="space-search"
          type="text"
          aria-label={t("search_space")}
          ref={inputRef}
          className={classNames(
            legacyClassNames("react-space-selector-dropdown__input"),
            "bg-tertiary placeholder:text-light placeholder:opacity-100",
          )}
          placeholder={placeholder}
          onChange={onSearch}
        />
      </div>
      {shouldDisplayHomepage && (
        <ul
          className={classNames(
            anchorClassNames("react-space-selector-dropdown__list"),
            "mt-4",
          )}
        >
          <li className="!p-0">
            <button
              type="button"
              className="flex w-full items-center gap-2 px-5 py-2"
              onClick={event => handleSpace(event, 0)}
            >
              <Icon type="20-feed-v3" size={20} />
              <span
                className={anchorClassNames(
                  "react-space-selector-dropdown__name",
                )}
              >
                {t("feed")}
              </span>
            </button>
          </li>
        </ul>
      )}
      {map(Object.entries(groupedSpaces), ([space_group_name, _spaces]) => (
        <div key={space_group_name}>
          <span
            className={legacyClassNames(
              "react-space-selector-dropdown__category",
            )}
          >
            {space_group_name}
          </span>
          <SpaceList spaces={_spaces} handleSpace={handleSpace} />
        </div>
      ))}
      {filterSpaces.length === 0 && <NoResultsListItem />}
    </DropdownMenu_DEPRECATED>
  );
};
